import {
  PureTable,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  styled,
  useTheme,
} from '@/theme/index';
import { getStateUrl } from '@/utils/unclaimedMoneyStates';
import { useScreenSize } from '@/utils/useScreenSize';
import { MobileUnclaimedMoneyResults } from './MobileUnclaimedMoneyResults';
import { UnclaimedMoneyRowsProps } from '../../interfaces';

const HeaderTableCell = styled(TableCell)<{ isLast: boolean }>(
  ({ theme, isLast }) => ({
    position: 'relative',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.4)',
    backgroundColor: theme.palette?.unclaimedMoneyGrid?.dark,
    borderBottom: `1px solid ${
      theme.palette?.customCard
        ? 'rgba(255, 255, 255, 0.4)'
        : theme.palette?.unclaimedMoneyGrid?.dark
    }`,

    '&::after': {
      content: '""',
      position: 'absolute',
      top: '30%',
      bottom: '30%',
      right: 0,
      width: '1px',
      backgroundColor: !isLast ? 'rgba(255, 255, 255, 0.4)' : 'transparent',
    },
  })
);

const BodyTableCell = styled(TableCell)<{
  isFirst: boolean;
  isLast: boolean;
  rowIndex: number;
}>(({ theme, isFirst, isLast, rowIndex }) => ({
  position: 'relative',
  textAlign: 'center',
  paddingLeft: theme.spacing(2),
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  maxWidth: theme.spacing(19),
  borderBottom: `2px solid ${
    rowIndex % 2 === 0
      ? theme.palette?.unclaimedMoneyGrid?.light
      : theme.palette?.unclaimedMoneyGrid?.contrastText
  }`,

  '&::before': {
    content: '""',
    position: 'absolute',
    left: '10px',
    top: '20%',
    bottom: '20%',
    width: theme.spacing(0.75),
    backgroundColor: isFirst
      ? theme.palette?.unclaimedMoneyGrid?.main
      : 'transparent',
    borderRadius: theme.spacing(0.5),
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '20%',
    bottom: '20%',
    right: 0,
    width: theme.spacing(0.125),
    backgroundColor: !isLast ? 'rgba(255, 255, 255, 0.4)' : 'transparent',
  },
}));

const ActionTableCell = styled(TableCell)<{ rowIndex: number }>(
  ({ theme, rowIndex }) => ({
    position: 'relative',
    textAlign: 'center',
    borderBottom: `2px solid ${
      rowIndex % 2 === 0
        ? theme.palette?.unclaimedMoneyGrid?.light
        : theme.palette?.unclaimedMoneyGrid?.contrastText
    }`,
  })
);

const ActionButton = styled(Button)(({ theme }) => ({
  width: theme.spacing(14.75),
  maxHeight: theme.spacing(3.75),
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  backgroundColor: theme.palette?.unclaimedMoneyGrid?.main,
}));

export interface UnclaimedMoneyResultsGridProps {
  /** The list of unclaimed money results */
  unclaimedMoneyRows: UnclaimedMoneyRowsProps;
}

/**
 * This component is responsible for displaying the unclaimed money results grid.
 */
export function UnclaimedMoneyResultsGrid({
  unclaimedMoneyRows,
}: UnclaimedMoneyResultsGridProps) {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  const onClickActionButton = () => {
    const stateUrl = getStateUrl(unclaimedMoneyRows.rows[0].heldIn || '');
    window.open(stateUrl, '_blank');
  };

  const headers = [
    'Name',
    'Address',
    'Reported By',
    'Held By',
    'Amount Held',
    'Date',
    'Action',
  ];

  return isMobile ? (
    <>
      {unclaimedMoneyRows.rows.map((row, index) => (
        <MobileUnclaimedMoneyResults
          key={index}
          unclaimedMoneyRow={row}
          rowIndex={index}
        />
      ))}
    </>
  ) : (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, overflow: 'hidden' }}
    >
      <PureTable sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <HeaderTableCell
                key={header}
                isLast={index === headers.length - 1}
              >
                {header}
              </HeaderTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {unclaimedMoneyRows.rows.map((row, rowIndex) => (
            <TableRow
              key={row.rawData?.id}
              sx={{
                backgroundColor:
                  rowIndex % 2 === 0
                    ? theme.palette?.unclaimedMoneyGrid?.light
                    : theme.palette?.unclaimedMoneyGrid?.contrastText,
                '&:last-child td, &:last-child th': {
                  border: 0,
                },
              }}
            >
              {Object.values(row).map((cell, cellIndex) => (
                <BodyTableCell
                  key={cellIndex}
                  isFirst={cellIndex === 0}
                  isLast={cellIndex === headers.length - 1}
                  rowIndex={rowIndex}
                >
                  {cell}
                </BodyTableCell>
              ))}
              <ActionTableCell rowIndex={rowIndex}>
                <ActionButton variant="contained" onClick={onClickActionButton}>
                  File claim
                </ActionButton>
              </ActionTableCell>
            </TableRow>
          ))}
        </TableBody>
      </PureTable>
    </TableContainer>
  );
}
